/* eslint-disable no-console */
import React, { useEffect, useState } from 'react';
import AdminLayout from './AdminLayout.component';
import {
  getTranslations,
  createTranslation,
  updateTranslation,
  deleteTranslation,
} from '../api/admin-translations.api';
import { getLocales } from '@/modules/core/client/utils/locales';
import i18n from '@/config/client/i18n';

export default function AdminTranslations() {
  const [language, setLanguage] = useState(i18n.language || 'en');
  const [namespace, setNamespace] = useState('translation');
  const [translations, setTranslations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [newKey, setNewKey] = useState('');
  const [newValue, setNewValue] = useState('');

  const locales = getLocales();

  const namespaces = [
    'translation',
    'core',
    'users',
    'pages',
    'cms',
    'offers',
    'contacts',
    'circles',
    'experiences',
    'messages',
    'search',
    'support',
    'statistics',
  ];

  const load = () => {
    setLoading(true);
    setError(null);

    getTranslations({ language, namespace })
      .then(data => {
        setTranslations(data);
      })
      .catch(() => {
        setError('Failed to load translations');
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const reloadI18nBundle = async (lang, ns) => {
    try {
      const response = await fetch(
        `/api/i18n/locales/${lang}/${ns}?v=${Date.now()}`,
        {
          cache: 'no-store',
          headers: {
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            Pragma: 'no-cache',
            Expires: '0',
          },
        },
      );

      if (response.ok) {
        const translationsData = await response.json();

        // Remove old bundle
        i18n.removeResourceBundle(lang, ns);

        // Add fresh bundle
        i18n.addResourceBundle(lang, ns, translationsData, true, true);

        // Update store data directly
        if (i18n.store && i18n.store.data) {
          if (!i18n.store.data[lang]) {
            i18n.store.data[lang] = {};
          }
          i18n.store.data[lang][ns] = translationsData;
        }

        return true;
      }
    } catch (err) {
      console.warn(`Failed to reload i18n bundle ${lang}/${ns}:`, err);
    }
    return false;
  };

  // Load translations when language or namespace changes
  useEffect(() => {
    load();
  }, [language, namespace]);

  // Change website language when admin selects a new language
  const handleLanguageChange = async event => {
    const newLanguage = event.target.value;

    try {
      // Set the state immediately for UI responsiveness
      setLanguage(newLanguage);

      // Change the actual website language
      await i18n.changeLanguage(newLanguage);

      // Set language cookie to persist language selection
      const setCookie = (name, value, days = 365) => {
        const expires = new Date();
        expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
        document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
      };

      setCookie('i18n', newLanguage);

      // Reload the current namespace in the new language
      await reloadI18nBundle(newLanguage, namespace);
    } catch (err) {
      console.error('Failed to change language:', err);
      setError('Failed to change website language');
    }
  };

  const handleNamespaceChange = event => {
    setNamespace(event.target.value);
  };

  const reloadTranslationsForCurrentLanguage = async () => {
    try {
      // Set language cookie
      const setCookie = (name, value, days = 365) => {
        const expires = new Date();
        expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);
        document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
      };

      setCookie('i18n', language);

      // Reload the edited language namespace
      await reloadI18nBundle(language, namespace);

      // Force i18n to emit a change event to trigger component re-renders
      const currentLang = i18n.language;
      await i18n.changeLanguage('__temp__');
      await i18n.changeLanguage(currentLang);

      // Re-set cookie
      setCookie('i18n', currentLang);
    } catch (err) {
      console.error('Failed to reload translations:', err);
    }
  };

  const handleCreate = async event => {
    event.preventDefault();
    if (!newKey || !newValue) return;

    try {
      const created = await createTranslation({
        namespace,
        key: newKey,
        language,
        value: newValue,
      });
      setTranslations(prev =>
        [...prev, created].sort((a, b) => a.key.localeCompare(b.key)),
      );
      setNewKey('');
      setNewValue('');
      setSuccess('Translation added successfully! Reloading...');
      setError(null);
      await reloadTranslationsForCurrentLanguage();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(
        'Failed to create translation (possibly duplicate key for this namespace & language)',
      );
      setSuccess(null);
    }
  };

  const handleUpdate = async (id, value) => {
    try {
      const updated = await updateTranslation(id, { value });
      setTranslations(prev =>
        prev.map(t => (t._id === id ? { ...t, value: updated.value } : t)),
      );
      setSuccess('Translation updated successfully! Reloading...');
      setError(null);
      await reloadTranslationsForCurrentLanguage();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to update translation');
      setSuccess(null);
    }
  };

  const handleEditClick = translationId => {
    const inputId = `translation-input-${translationId}`;
    const inputElement = document.getElementById(inputId);
    if (inputElement) {
      inputElement.focus();
      inputElement.select();
    }
  };

  const handleDelete = async id => {
    if (!window.confirm('Delete this translation?')) return;

    try {
      await deleteTranslation(id);
      setTranslations(prev => prev.filter(t => t._id !== id));
      setSuccess('Translation deleted successfully! Reloading...');
      setError(null);
      await reloadTranslationsForCurrentLanguage();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError('Failed to delete translation');
      setSuccess(null);
    }
  };

  const handleRefreshTranslations = async () => {
    try {
      setSuccess('Refreshing translations...');

      // Reload current namespace
      await reloadI18nBundle(language, namespace);

      // Also reload the namespace for the current UI language if different
      if (i18n.language !== language) {
        await reloadI18nBundle(i18n.language, namespace);
      }

      // Force a language change to trigger UI updates
      const currentLang = i18n.language;
      await i18n.changeLanguage('__temp__');
      await i18n.changeLanguage(currentLang);

      setSuccess('Translations refreshed!');
      setTimeout(() => setSuccess(null), 2000);
    } catch (err) {
      setError('Failed to refresh translations');
      setTimeout(() => setError(null), 3000);
    }
  };

  return (
    <AdminLayout>
      <div className="container">
        <div className="row" style={{ marginBottom: '1rem' }}>
          <div className="col-sm-9">
            <h2>Translations</h2>
            <p>Manage i18n strings dynamically without redeploying.</p>
            <p>
              <strong>Current UI language:</strong> {i18n.language}
              {i18n.language !== language && (
                <span style={{ color: '#666', marginLeft: '10px' }}>
                  (Editing: {language})
                </span>
              )}
            </p>
          </div>
          <div className="col-sm-3" style={{ marginTop: '20px' }}>
            <button
              type="button"
              className="btn btn-default btn-block"
              onClick={handleRefreshTranslations}
            >
              Refresh Translations
            </button>
          </div>
        </div>

        {error && <div className="alert alert-danger">{error}</div>}
        {success && <div className="alert alert-success">{success}</div>}

        <div className="row" style={{ marginBottom: '1rem' }}>
          <div className="col-sm-3">
            <label htmlFor="admin-translations-language">Language</label>
            <select
              id="admin-translations-language"
              className="form-control"
              value={language}
              onChange={handleLanguageChange}
            >
              {locales.map(locale => (
                <option key={locale.code} value={locale.code}>
                  {locale.label} ({locale.code})
                </option>
              ))}
            </select>
          </div>
          <div className="col-sm-3">
            <label htmlFor="admin-translations-namespace">Namespace</label>
            <select
              id="admin-translations-namespace"
              className="form-control"
              value={namespace}
              onChange={handleNamespaceChange}
            >
              {namespaces.map(ns => (
                <option key={ns} value={ns}>
                  {ns}.json
                </option>
              ))}
            </select>
          </div>
        </div>

        <form className="panel panel-default" onSubmit={handleCreate}>
          <div className="panel-heading">Add new translation</div>
          <div className="panel-body row">
            <div className="col-sm-4">
              <label htmlFor="admin-translations-new-key">Key</label>
              <input
                id="admin-translations-new-key"
                type="text"
                className="form-control"
                value={newKey}
                onChange={event => setNewKey(event.target.value)}
                placeholder="e.g. homepage.welcome"
              />
            </div>
            <div className="col-sm-6">
              <label htmlFor="admin-translations-new-value">Value</label>
              <input
                id="admin-translations-new-value"
                type="text"
                className="form-control"
                value={newValue}
                onChange={event => setNewValue(event.target.value)}
                placeholder="Translated text"
              />
            </div>
            <div className="col-sm-2" style={{ marginTop: '24px' }}>
              <button type="submit" className="btn btn-primary btn-block">
                Add
              </button>
            </div>
          </div>
        </form>

        {loading ? (
          <p>Loading translations…</p>
        ) : (
          <table className="table table-striped">
            <thead>
              <tr>
                <th>Key</th>
                <th>Value</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {translations.map(t => (
                <tr key={t._id}>
                  <td>
                    <code>{t.key}</code>
                  </td>
                  <td>
                    <input
                      id={`translation-input-${t._id}`}
                      type="text"
                      className="form-control"
                      defaultValue={t.value}
                      onBlur={event => {
                        if (event.target.value !== t.value) {
                          handleUpdate(t._id, event.target.value);
                        }
                      }}
                      onKeyDown={event => {
                        if (event.key === 'Enter') {
                          event.target.blur();
                        }
                      }}
                    />
                  </td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-primary btn-xs"
                      onClick={() => handleEditClick(t._id)}
                      style={{ marginRight: '5px', minWidth: '50px' }}
                    >
                      Edit
                    </button>
                    <button
                      type="button"
                      className="btn btn-danger btn-xs"
                      onClick={() => handleDelete(t._id)}
                      style={{ minWidth: '50px' }}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
              {!translations.length && (
                <tr>
                  <td colSpan="3">
                    No translations found for this language and namespace.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </AdminLayout>
  );
}

AdminTranslations.propTypes = {};
